import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACKS = [
  { id: "academic", label: "🎓 Academic Foundations" },
  { id: "zion", label: "✡️ Zion Pillars" },
];

const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

function Bar({ value, color = "#FFD700" }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div
      style={{
        width: "100%",
        height: 8,
        background: "#1a1a1a",
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid #333",
      }}
    >
      <div
        style={{
          width: `${v}%`,
          height: "100%",
          background: color,
          transition: "width 400ms ease",
        }}
      />
    </div>
  );
}

export default function K12GradeProgress({ onBack }) {
  const [track, setTrack] = useState("academic");
  const [grade, setGrade] = useState("K");
  const [summary, setSummary] = useState(null);
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/k12-program/progress/summary`, { headers: authHeaders() });
      setSummary(r.data);
    } catch (e) {
      console.warn("summary load failed", e);
    }
  }, []);

  const loadGrade = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/k12-program/progress/${track}/${grade}`, {
        headers: authHeaders(),
      });
      setGrid(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load progress");
      setGrid(null);
    } finally {
      setLoading(false);
    }
  }, [track, grade]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadGrade();
  }, [loadGrade]);

  return (
    <div data-testid="k12-progress-dashboard" style={{ maxWidth: 1100, margin: "0 auto", padding: 20, paddingBottom: 110 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h2 style={{ color: "#FFD700", margin: 0, fontSize: "1.7rem" }}>Grade Progress</h2>
          <div style={{ color: "#aaa", fontSize: ".95rem" }}>
            Track every subject's lesson completion, quiz mastery, and end-of-grade exams.
          </div>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            data-testid="progress-back-btn"
            style={{ background: "#333", padding: "10px 18px", color: "#fff" }}
          >
            ← Back
          </button>
        )}
      </div>

      {/* Overall summary card */}
      {summary && (
        <div
          className="card"
          data-testid="progress-summary-card"
          style={{
            background: "linear-gradient(135deg, #1a1a0a 0%, #2a200a 100%)",
            borderLeft: "4px solid #FFD700",
            marginBottom: 20,
          }}
        >
          <h3 style={{ color: "#FFD700", marginTop: 0, marginBottom: 14 }}>Overall · All Grades · All Tracks</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            <Stat label="Lessons Completed" value={summary.total_lessons_completed} />
            <Stat label="Quizzes Taken" value={summary.total_quizzes_taken} />
            <Stat label="Quizzes Passed" value={summary.quizzes_passed} />
            <Stat label="Avg Quiz Score" value={summary.total_quizzes_taken ? `${summary.average_quiz_score}%` : "—"} />
            <Stat label="Final Exams Passed" value={`${summary.final_exams_passed} / ${summary.total_final_exams_taken}`} />
            <Stat label="Subjects Touched" value={summary.subjects_touched} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {TRACKS.map((t) => (
            <button
              key={t.id}
              data-testid={`progress-track-${t.id}`}
              onClick={() => setTrack(t.id)}
              style={{
                padding: "10px 16px",
                background: track === t.id ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)" : "#222",
                color: track === t.id ? "#000" : "#ddd",
                fontWeight: track === t.id ? "bold" : "normal",
                width: "auto",
                flex: "0 0 auto",
                border: `1px solid ${track === t.id ? "#FFD700" : "#444"}`,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {GRADES.map((g) => (
            <button
              key={g}
              data-testid={`progress-grade-${g}`}
              onClick={() => setGrade(g)}
              style={{
                padding: "8px 12px",
                background: grade === g ? "#FFD700" : "#222",
                color: grade === g ? "#000" : "#ddd",
                fontWeight: grade === g ? "bold" : "normal",
                minWidth: 40,
                width: "auto",
                flex: "0 0 auto",
                border: `1px solid ${grade === g ? "#FFD700" : "#444"}`,
              }}
            >
              {g === "K" ? "K" : `Grade ${g}`}
            </button>
          ))}
        </div>
      </div>

      {/* Grade overall card */}
      {grid?.overall && (
        <div
          className="card"
          data-testid="progress-grade-overall"
          style={{ background: "#0f0f0f", marginBottom: 16 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <h3 style={{ color: "#FFD700", margin: 0 }}>
              {grid.grade_name} · {grid.track === "academic" ? "Academic Foundations" : "Zion Pillars"}
            </h3>
            <div style={{ color: "#ddd" }}>
              <span style={{ color: "#FFD700", fontWeight: "bold" }}>{grid.overall.completion_percent}%</span>{" "}
              complete
            </div>
          </div>
          <Bar value={grid.overall.completion_percent} />
          <div style={{ display: "flex", gap: 20, marginTop: 12, color: "#aaa", fontSize: ".9rem", flexWrap: "wrap" }}>
            <span>
              Lessons: <strong style={{ color: "#fff" }}>{grid.overall.completed_lessons}/{grid.overall.total_lessons}</strong>
            </span>
            <span>
              Avg Quiz Score:{" "}
              <strong style={{ color: "#fff" }}>
                {grid.overall.average_quiz_score != null ? `${grid.overall.average_quiz_score}%` : "—"}
              </strong>
            </span>
            <span>
              Finals Passed:{" "}
              <strong style={{ color: "#fff" }}>
                {grid.overall.subjects_with_passed_final} / {grid.overall.subjects_count}
              </strong>
            </span>
          </div>
        </div>
      )}

      {/* Per-subject cards */}
      {loading && <div style={{ color: "#aaa", padding: 20 }}>Loading progress…</div>}
      {error && <div style={{ color: "#ff6b6b", padding: 16, background: "#2a0000", borderRadius: 8 }}>{error}</div>}
      {grid?.subjects?.length === 0 && !loading && (
        <div style={{ color: "#aaa", padding: 24, textAlign: "center" }}>
          No subjects defined for this grade in this track.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
        {grid?.subjects?.map((s) => (
          <div
            key={s.subject_id}
            className="card"
            data-testid={`progress-subject-${s.subject_id}`}
            style={{ borderLeft: `4px solid ${s.color || "#FFD700"}`, background: "#0f0f0f" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: "1.8rem" }}>{s.icon || "📘"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: s.color || "#FFD700", fontWeight: "bold", fontSize: "1.1rem" }}>{s.name}</div>
                <div style={{ color: "#888", fontSize: ".8rem" }}>
                  {s.completed_lessons} of {s.total_lessons} lessons
                </div>
              </div>
              <div style={{ color: "#FFD700", fontWeight: "bold", fontSize: "1.3rem" }}>
                {s.completion_percent}%
              </div>
            </div>
            <Bar value={s.completion_percent} color={s.color || "#FFD700"} />
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: ".85rem" }}>
              <div>
                <div style={{ color: "#888" }}>Quizzes taken</div>
                <div style={{ color: "#fff" }}>{s.quizzes_taken}</div>
              </div>
              <div>
                <div style={{ color: "#888" }}>Passed (≥70%)</div>
                <div style={{ color: "#fff" }}>{s.lessons_with_passing_quiz}</div>
              </div>
              <div>
                <div style={{ color: "#888" }}>Avg quiz score</div>
                <div style={{ color: "#fff" }}>
                  {s.average_quiz_score != null ? `${s.average_quiz_score}%` : "—"}
                </div>
              </div>
              <div>
                <div style={{ color: "#888" }}>Final exam</div>
                <div
                  style={{
                    color: s.final_exam_passed ? "#5ec65e" : s.final_exam_attempts > 0 ? "#ff9f43" : "#888",
                    fontWeight: "bold",
                  }}
                >
                  {s.final_exam_passed
                    ? `✓ Passed (${s.final_exam_best_score}%)`
                    : s.final_exam_attempts > 0
                    ? `${s.final_exam_attempts} attempt${s.final_exam_attempts > 1 ? "s" : ""} · best ${s.final_exam_best_score}%`
                    : "Not taken"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ color: "#888", fontSize: ".8rem", textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
      <div style={{ color: "#FFD700", fontSize: "1.6rem", fontWeight: "bold" }}>{value}</div>
    </div>
  );
}
